import React, { useEffect, useMemo, useState } from "react";
import { Backdrop, CircularProgress } from '@mui/material';
import styles from "./registerevent.module.css";
import wordmark from "./registereventassets/sunbaewordmark.svg";
import starbucksImg from "./registereventassets/starbucks.png";
import payssamLogo from "./registereventassets/payssamlogo.svg";
import { drawReceiptImage, ReceiptData } from "./receiptcanvas";
import ReceiptViewer, { uploadReceiptImage } from "./receiptviewer";

//네이버 영수증 등록(리뷰 쓰기) 공통 URL
const NAVER_REVIEW_URL = "https://m.place.naver.com/my/checkin";

//메디컬북 소개 영상 (R2)
const MEDICAL_VIDEO_URL = "https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/blog/1/f_c_f_mp_.mp4";

interface StepProps {
    n: number;
    title: string;
    desc: string;
    state: "done" | "open" | "locked";
    expanded: boolean;
    onToggle: () => void;
    children?: React.ReactNode;
}

//참여 단계 카드 (완료/진행중/잠김)
const Step: React.FC<StepProps> = ({ n, title, desc, state, expanded, onToggle, children }) => {

    const cls = `${styles.step} ${state === "done" ? styles.stepDone : state === "open" ? styles.stepOpen : styles.stepLocked}`;
    const label = state === "done" ? "완료" : state === "open" ? "진행 중" : "잠김";
    const reopenable = state === "done";
    const showBody = state === "open" || (state === "done" && expanded);

    const headContent = (
        <>
            <span className={styles.stepMark}>{state === "done" ? "✓" : String(n).padStart(2, "0")}</span>
            <span className={styles.stepLabel}>
                <span className={styles.stepTitle}>{title}</span>
                <span className={styles.stepDesc}>{desc}</span>
            </span>
            <span className={styles.stepState}>{label}</span>
        </>
    );

    return (
        <div className={cls}>
            {
                reopenable
                    ?
                    <button type="button" className={`${styles.stepHead} ${styles.stepHeadReopenable}`} onClick={onToggle} aria-expanded={expanded}>
                        {headContent}
                    </button>
                    :
                    <div className={styles.stepHead}>
                        {headContent}
                    </div>
            }
            {showBody && children && <div className={styles.stepBody}>{children}</div>}
        </div>
    );
};

const RegisterEvent: React.FC<any> = (props) => {

    //카카오 알림톡 링크 : peetsunbae.com/#/registerevent?bill_id=xxx&code=xxx
    const { billId, codeParam } = useMemo(() => {
        const url = window.location.href;
        const query = url.split("?bill_id=")[1];

        if (!query) {
            return { billId: "", codeParam: "" };
        }

        return {
            billId: query.split("&code=")[0] || "",
            codeParam: query.split("&code=")[1] || ""
        };
    }, []);

    const storageKey = `sunbae.reviewEvent.${billId}`;

    //네이버 다녀오는 사이 탭이 날아가도 진행상태 유지
    const saved = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem(storageKey) || "{}") || {};
        } catch (e) {
            return {};
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [loading, setLoading] = useState(true);
    const [errorState, setErrorState] = useState<"" | "invalid" | "notPaid" | "error">("");
    const [apprState, setApprState] = useState("");
    const [receiptImg, setReceiptImg] = useState<{ dataUrl: string, blob: Blob | null } | null>(null);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
    const [viewerOpen, setViewerOpen] = useState(false);

    const [gift, setGift] = useState(saved.gift || "medical");
    const [receiptDone, setReceiptDone] = useState(!!saved.receiptDone);
    const [reviewDone, setReviewDone] = useState(!!saved.reviewDone);
    const [nickname, setNickname] = useState(saved.nickname || "");
    const [me, setMe] = useState({ name: saved.name || "", phone: saved.phone || "" });
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(!!saved.submitted);
    const [submitting, setSubmitting] = useState(false);
    const [openStep, setOpenStep] = useState(0);

    const toggle = (n: number) => setOpenStep((value) => (value === n ? 0 : n));

    const gifts: any[] = [
        { id: "medical", name: "메디컬북", sub: "합격 멘토 296인의 점수 · 진도 · 노하우 데이터북", video: MEDICAL_VIDEO_URL, sticker: true },
        { id: "starbucks", name: "스타벅스 아메리카노 T 1잔", sub: "모바일 교환권으로 발송됩니다", image: starbucksImg, sticker: false },
    ];

    const selectedGift = gifts.find((g) => g.id === gift);
    const giftName = selectedGift ? selectedGift.name : "";

    const canSubmit = me.name.trim() && me.phone.trim() && nickname.trim() && file && !submitting;

    useEffect(() => {

        document.title = "수능선배 등록 인증 EVENT";

        const meta = document.createElement("meta");
        meta.name = "viewport";
        meta.content = "width=device-width, initial-scale=1";
        document.getElementsByTagName("head")[0].appendChild(meta);

        if (!billId || !codeParam || !/^\d{20}$/.test(billId)) {
            setErrorState("invalid");
            setLoading(false);
            return;
        }

        fetch("https://peetsunbae.com/api/payments/billreceipt", {
            method: "post",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ bill_id: billId, code: codeParam })
        }).then((response: any) => {
            response.json().then(async (result: any) => {

                if (result.message === "success" && result.data) {

                    try {
                        const img = await drawReceiptImage(result.data as ReceiptData, payssamLogo);
                        setReceiptImg(img);

                        //https 이미지 URL로 만들어두면 카톡 등 인앱 브라우저에서 길게 눌러 사진 저장 가능 (실패해도 dataURL로 동작)
                        if (img.blob) {
                            uploadReceiptImage(billId, codeParam, img.blob).then((url) => {
                                if (url) {
                                    setReceiptUrl(url);
                                }
                            });
                        }
                    } catch (e) {
                        console.log(e);
                        setErrorState("error");
                    }

                    setLoading(false);
                    return;
                }

                if (result.message === "notPaid" && result.data) {
                    setApprState(result.data.apprState || "");
                    setErrorState("notPaid");
                    setLoading(false);
                    return;
                }

                setErrorState("invalid");
                setLoading(false);

            }).catch(() => {
                setErrorState("error");
                setLoading(false);
            });
        }).catch(() => {
            setErrorState("error");
            setLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //진행상태 저장
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                gift, receiptDone, reviewDone, nickname, name: me.name, phone: me.phone, submitted,
            }));
        } catch (e) { }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gift, receiptDone, reviewDone, nickname, me.name, me.phone, submitted]);

    //첨부한 캡처 미리보기
    useEffect(() => {
        if (!file) {
            setPreview(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    //010-0000-0000 자동 하이픈
    const formatPhone = (raw: string) => {
        const digits = raw.replace(/\D/g, "").slice(0, 11);
        if (digits.length < 4) return digits;
        if (digits.length < 8) return digits.slice(0, 3) + "-" + digits.slice(3);
        return digits.slice(0, 3) + "-" + digits.slice(3, 7) + "-" + digits.slice(7);
    };

    //영수증 이미지 저장
    //1) 공유시트 지원 브라우저(사파리·크롬)는 공유창에서 '이미지 저장' -> 사진첩
    //2) PC는 파일 다운로드
    //3) 카톡 등 인앱 브라우저는 blob 다운로드가 안 되므로 크게 보기를 열어 "길게 눌러 이미지 저장" 유도
    const saveReceipt = async () => {

        if (!receiptImg) {
            return;
        }

        setReceiptDone(true);

        const nav: any = navigator;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (receiptImg.blob && nav.canShare) {
            try {
                const imageFile = new File([receiptImg.blob], "수능선배_영수증.png", { type: "image/png" });

                if (nav.canShare({ files: [imageFile] })) {
                    await nav.share({ files: [imageFile] });
                    return;
                }
            } catch (e: any) {
                if (e && e.name === "AbortError") {
                    //사용자가 공유창을 직접 닫음 - 추가 동작 없음
                    return;
                }
                //그 외 실패는 아래 폴백으로
            }
        }

        if (!isMobile && receiptImg.blob) {
            try {
                const url = URL.createObjectURL(receiptImg.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "수능선배_영수증.png";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 10000);
                return;
            } catch (e) {
                console.log(e);
            }
        }

        setViewerOpen(true);
    };

    //리뷰 인증 제출
    const submit = async () => {

        if (!canSubmit || !file) {
            return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("bill_id", billId);
            formData.append("code", codeParam);
            formData.append("name", me.name.trim());
            formData.append("phone", me.phone.trim());
            formData.append("nickname", nickname.trim());
            formData.append("gift", gift);
            formData.append("image", file);

            const response = await fetch("https://peetsunbae.com/api/payments/reviewevent/submit", {
                method: "post",
                body: formData
            });

            const result = await response.json();

            if (result.message === "success") {
                setSubmitted(true);
                setOpenStep(0);
            } else {
                alert("제출에 실패했어요. 잠시 후 다시 시도해주세요.");
            }
        } catch (e) {
            console.log(e);
            alert("제출에 실패했어요. 잠시 후 다시 시도해주세요.");
        }

        setSubmitting(false);
    };

    const s1: "done" | "open" | "locked" = receiptDone ? "done" : "open";
    const s2: "done" | "open" | "locked" = reviewDone ? "done" : receiptDone ? "open" : "locked";
    const s3: "done" | "open" | "locked" = submitted ? "done" : reviewDone ? "open" : "locked";

    const notPaidMessage = apprState === "C" ? "취소된 결제예요.\n이벤트는 결제 완료된 건에 한해 참여할 수 있어요."
        : apprState === "D" ? "파기된 청구서예요.\n이벤트는 결제 완료된 건에 한해 참여할 수 있어요."
            : "아직 결제가 완료되지 않았어요.\n결제 완료 후 다시 접속해주세요.";

    return (
        <div className={styles.stage}>
            <div className={styles.frame}>

                <header className={styles.header}>
                    <img src={wordmark} alt="수능선배" className={styles.headerLogo} />
                </header>

                {
                    errorState
                        ?
                        <div className={styles.notice}>
                            <p className={styles.noticeTitle}>
                                {errorState === "notPaid" ? "참여할 수 없는 결제 건이에요" : errorState === "invalid" ? "잘못된 접근이에요" : "일시적인 오류가 발생했어요"}
                            </p>
                            <p className={styles.noticeDesc}>
                                {
                                    errorState === "notPaid"
                                        ? notPaidMessage
                                        : errorState === "invalid"
                                            ? "알림톡의 [등록인증 이벤트] 버튼으로 다시 접속해주세요."
                                            : "잠시 후 다시 시도해주세요.\n문제가 계속되면 카카오톡 채널로 문의해주세요."
                                }
                            </p>
                        </div>
                        :
                        <>
                            <div className={styles.eventHeader}>
                                <h2 className={styles.eventEyebrow}>{"수능선배 등록 인증\nEVENT"}</h2>
                                <hr className={styles.hair} />
                            </div>

                            <div className={styles.summaryWrap}>
                                <div className={styles.summary}>
                                    <div className={styles.summaryTitle}>수능선배 등록 인증 EVENT</div>
                                    <hr className={styles.summaryHair} />
                                    <div className={styles.summaryRows}>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.bullet}>·</span>
                                            <span className={styles.summaryKey}>대상</span>
                                            <span className={styles.summaryVal}>수능선배 등록을 마치신 분</span>
                                        </div>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.bullet}>·</span>
                                            <span className={styles.summaryKey}>조건</span>
                                            <span className={styles.summaryVal}>네이버 영수증 리뷰 50자 이상 작성</span>
                                        </div>
                                        <div className={styles.summaryRow}>
                                            <span className={styles.bullet}>·</span>
                                            <span className={styles.summaryKey}>발송</span>
                                            <span className={styles.summaryVal}>확인 후 2주 이내 발송</span>
                                        </div>
                                    </div>
                                    <hr className={styles.summaryHair} />
                                    <p className={styles.summaryFootnote}>본 결제 건에 한하여 1회 참여 가능합니다.</p>
                                </div>
                            </div>

                            {/* 혜택 선택 */}
                            <div className={styles.cntHeading}>
                                <h2 className={styles.cntHeadingTitle}>{"참여자 전원에게\n드리는 혜택"}</h2>
                            </div>

                            <div className={styles.giftList} role="radiogroup" aria-label="혜택 선택">
                                {gifts.map((g) => (
                                    <div key={g.id} className={`${styles.gift} ${gift === g.id ? styles.giftSelected : ""}`}>
                                        <div className={styles.giftMedia}>
                                            {
                                                g.video
                                                    ? <video className={styles.giftVideo} src={g.video} autoPlay muted loop playsInline preload="auto" />
                                                    : <img className={styles.giftImage} src={g.image} alt={g.name} />
                                            }
                                            {
                                                g.sticker &&
                                                <div className={styles.sticker}>
                                                    <div className={styles.stickerEn}>SUNBAE</div>
                                                    <div className={styles.stickerSub}>독점 제공</div>
                                                </div>
                                            }
                                        </div>
                                        <button type="button" role="radio" aria-checked={gift === g.id}
                                            className={styles.giftBody} onClick={() => setGift(g.id)}>
                                            <span className={styles.giftRadio}></span>
                                            <span>
                                                <span className={styles.giftName}>{g.name}</span>
                                                <span className={styles.giftSub}>{g.sub}</span>
                                            </span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <p className={styles.giftNote}>원하시는 경품을 선택해주세요. 제출 후에는 변경이 어려울 수 있습니다.</p>

                            {/* 참여 단계 */}
                            <div className={styles.cntHeading}>
                                <h2 className={styles.cntHeadingTitle}>이벤트 참여 방법</h2>
                            </div>

                            <div className={styles.steps}>
                                <Step n={1} state={s1} expanded={openStep === 1} onToggle={() => toggle(1)}
                                    title="영수증 이미지 저장하기"
                                    desc="네이버 영수증 리뷰는 영수증 등록 후에 작성할 수 있어요.">
                                    <div className={styles.receiptBox}>
                                        {receiptImg && <img src={receiptUrl || receiptImg.dataUrl} alt="결제 영수증" />}
                                    </div>
                                    <button type="button" className={styles.btn} disabled={!receiptImg} onClick={saveReceipt}>
                                        영수증 저장<span className={styles.btnArrow}>↓</span>
                                    </button>
                                    <p className={styles.hint}>공유 창이 뜨면 <b>‘이미지 저장’</b>을 선택해 주세요. 영수증 크게 보기가 열리면 이미지를 <b>길게 눌러 ‘이미지 저장’</b>을 선택하면 사진첩에 들어갑니다.</p>
                                    <p className={styles.hint}>저장한 사진을 네이버 지도 › 영수증 등록에 그대로 올리면 리뷰 작성이 열립니다.</p>
                                </Step>

                                <Step n={2} state={s2} expanded={openStep === 2} onToggle={() => toggle(2)}
                                    title="네이버 리뷰 작성"
                                    desc="받은 영수증을 등록한 뒤, 50자 이상으로 리뷰를 작성해주세요.">
                                    <a className={`${styles.btn} ${styles.btnGhost}`} href={NAVER_REVIEW_URL} target="_blank" rel="noopener noreferrer"
                                        onClick={() => setReviewDone(true)}>
                                        네이버 영수증 리뷰 쓰기<span className={styles.btnArrow}>↗</span>
                                    </a>
                                    <p className={styles.hint}><b>50자 이상</b>으로 남기셔야 검수를 통과해요. 리뷰를 남긴 뒤 이 화면으로 돌아오시면 마지막 단계가 열립니다.</p>
                                </Step>

                                <Step n={3} state={s3} expanded={openStep === 3} onToggle={() => toggle(3)}
                                    title="리뷰 인증 제출"
                                    desc={submitted ? "제출이 접수되었습니다." : "네이버 닉네임과 리뷰 작성 화면을 캡처하여 제출해주세요."}>
                                    <>
                                        <div className={styles.row2}>
                                            <input className={styles.input} placeholder="성명" value={me.name}
                                                onChange={(e) => setMe({ ...me, name: e.target.value })} />
                                            <input className={styles.input} placeholder="010-0000-0000" inputMode="numeric"
                                                type="tel" maxLength={13} value={me.phone}
                                                onChange={(e) => setMe({ ...me, phone: formatPhone(e.target.value) })} />
                                        </div>
                                        <input className={styles.input} placeholder="리뷰에 표시된 네이버 닉네임"
                                            value={nickname} onChange={(e) => setNickname(e.target.value)} />
                                        <span className={styles.upload}>
                                            <span className={`${styles.uploadBox} ${preview ? styles.uploadBoxPreview : ""}`}>
                                                {
                                                    preview
                                                        ?
                                                        <>
                                                            <img className={styles.uploadPreview} src={preview} alt="첨부한 리뷰 인증 화면" />
                                                            <span className={styles.uploadRecap}>
                                                                <b>첨부 완료</b>
                                                                <span>다시 누르면 변경됩니다</span>
                                                            </span>
                                                        </>
                                                        :
                                                        <>
                                                            <span className={styles.uploadMain}>리뷰 작성 화면 캡처</span>
                                                            <span className={styles.uploadSub}>닉네임이 보이는 리뷰 화면 · JPG, PNG</span>
                                                        </>
                                                }
                                            </span>
                                            <input type="file" accept="image/*" aria-label="리뷰 인증 화면 첨부"
                                                onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                                        </span>
                                        <p className={styles.picked}>선택한 혜택 — <b>{giftName}</b></p>
                                        <p className={styles.hint}>반드시 공개 상태로 남겨 주세요. 비공개 리뷰는 참여로 인정되지 않습니다.</p>
                                    </>
                                </Step>
                            </div>

                            {
                                submitted &&
                                <p className={styles.doneNote}>제출이 접수되었습니다. {gift === "medical" ? `확인 후 2주 이내 ${giftName}을 학생에게 전달해드립니다.` : `확인 후 2주 이내 ${giftName}을 보내드립니다.`}</p>
                            }

                            <div className={styles.terms}>
                                <p className={styles.termsTitle}>유의사항</p>
                                <ul className={styles.termsList}>
                                    <li>50자 미만의 짧은 글, 성의 없는 내용, 사실과 다른 내용 등은 검수 후 혜택 지급이 제한될 수 있습니다.</li>
                                    <li>네이버 영수증 리뷰는 영수증을 등록한 본인 계정으로만 작성할 수 있습니다.</li>
                                    <li>비공개 리뷰는 이벤트 참여로 인정되지 않습니다.</li>
                                    <li>혜택은 리뷰 확인 후 2주 이내 발송되며, 주말 및 공휴일에는 발송이 지연될 수 있습니다.</li>
                                    <li>혜택 발송 전 리뷰가 삭제될 경우 혜택 지급이 취소될 수 있습니다.</li>
                                    <li>제출하신 이름·연락처·닉네임·인증 이미지는 이벤트 참여 확인 및 혜택 발송 목적으로만 사용됩니다.</li>
                                    <li>이벤트 관련 문의 — 재원생 앱 메시지 또는 대표번호 1668-5786</li>
                                </ul>
                            </div>

                            {
                                (s3 !== "locked" && (openStep === 3 || !submitted)) &&
                                <div className={styles.bar}>
                                    <button type="button" className={styles.btn} disabled={!canSubmit} onClick={submit}>
                                        {submitting ? "제출 중..." : submitted ? "다시 제출하기" : "인증 제출하기"}
                                    </button>
                                    <p className={styles.barNote}>
                                        {
                                            submitted
                                                ? (gift === "medical" ? `확인 후 2주 이내 ${giftName}을 학생에게 전달해드립니다.` : `확인 후 2주 이내 ${giftName}을 보내드립니다.`)
                                                : !me.name.trim() || !me.phone.trim()
                                                    ? "성명과 연락처를 입력해 주세요."
                                                    : !nickname.trim()
                                                        ? "네이버 닉네임을 입력해 주세요."
                                                        : !file
                                                            ? "인증 화면 캡처를 첨부해 주세요."
                                                            : `선택한 혜택 — ${giftName}`
                                        }
                                    </p>
                                </div>
                            }
                        </>
                }

                <ReceiptViewer
                    open={viewerOpen}
                    imgSrc={receiptUrl || (receiptImg ? receiptImg.dataUrl : "")}
                    onClose={() => setViewerOpen(false)}
                />

                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </div>
    );
};

export default RegisterEvent;
